import { io, type Socket } from 'socket.io-client';
import { API_ORIGIN } from '../config/env';
import { getAccessToken } from './client';

/**
 * The booking changing, pushed rather than waited for.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS IS FOR, AND WHAT IT IS NOT FOR
 * ---------------------------------------------------------------------------
 * Everything on a live booking already refreshes on a timer - the list every
 * thirty seconds, an open extension request every five. That works, and it
 * stays. What it cannot do is be quick about the two moments somebody is
 * actually sitting there watching the screen for:
 *
 *   "Finding your professional"  ->  a name and a face
 *   "Arranging the extra hour"   ->  pay now, or it is confirmed
 *
 * On both, the wait is the whole experience, and up to thirty seconds of it
 * was the poll's schedule rather than anything happening at Homingo.
 *
 * So this is an ACCELERATOR, not a transport. Nothing is only delivered here.
 * Every poll this sits beside is still running at its old cadence, deliberately
 * unchanged: a socket is the first thing a hotel wifi or a corporate proxy
 * breaks, and a customer on one of those must keep the behaviour they have
 * today rather than inherit a screen that updates only when it works.
 *
 * ---------------------------------------------------------------------------
 * WHY A MODULE AND NOT A HOOK
 * ---------------------------------------------------------------------------
 * Two places want this at once - the bookings list, and the extension card on
 * whichever booking is open - and they mount and unmount independently. A hook
 * that owned its own socket would open one per caller: two handshakes, two
 * authentications, two reconnect timers, for one phone. So the socket lives
 * here, reference-counted by who is watching, and `useBookingEvents` is a thin
 * wrapper over it.
 *
 * ---------------------------------------------------------------------------
 * WHAT COMES DOWN IT
 * ---------------------------------------------------------------------------
 * The news, not the booking. The server sends an id and what kind of change it
 * was; the app refetches through the API it already uses. Pushing the booking
 * itself would make this socket a second place that decides what a customer is
 * allowed to see, and the first one has the permission checks.
 */

/** What the gateway sends on the `booking` event. */
export type BookingChanged = {
  bookingId: string;
  /**
   * `status`    - the booking moved (assigned, en route, started, finished).
   * `extension` - an extra-hours request changed.
   * `resync`    - NOT an event. The socket has just joined this room, so
   *               anything that happened while it was down was never
   *               delivered and the caller should read the booking once.
   */
  kind: 'status' | 'extension' | 'resync';
  status?: string;
  from?: string;
  at: string;
};

type Listener = (message: BookingChanged) => void;

/** Where a refused handshake resumes from, and how far it is allowed to back off. */
const RETRY_BASE_MS = 3000;
const RETRY_MAX_MS = 60_000;

/** Who is watching what. A booking with no listeners is left, not kept. */
const watchers = new Map<string, Set<Listener>>();

let socket: Socket | null = null;

/**
 * Set while a refused handshake is waiting to be retried.
 *
 * Socket.IO reconnects on its own after a dropped connection, but NOT after the
 * server rejects the handshake - a middleware refusal leaves `socket.active`
 * false and the client sitting there for ever. That is the right default for a
 * bad URL and the wrong one for an expired token, which is the refusal this app
 * will actually hit: the access token turns over while the app is open, and the
 * next `getAccessToken()` would succeed. So that one case is retried here.
 */
let retry: ReturnType<typeof setTimeout> | null = null;
let retryDelayMs = RETRY_BASE_MS;

function announce(message: BookingChanged): void {
  const listeners = watchers.get(message.bookingId);
  if (!listeners) return;
  /* Copied before iterating: a listener is allowed to stop watching from
     inside its own callback, and mutating the set mid-loop would skip the one
     after it. */
  for (const listener of [...listeners]) {
    try {
      listener(message);
    } catch {
      /* One screen's reaction failing must not stop the others hearing it. */
    }
  }
}

/** Ask the server to put this socket in a booking's room, and prove we may. */
function join(bookingId: string): void {
  socket?.emit('track', { bookingId }, (reply?: { ok?: boolean }) => {
    /*
     * Refused means refused - someone else's booking, or one that is over.
     * Nothing to recover and nothing to tell the customer: the polls that are
     * still running are the authority on what this booking is.
     */
    if (!reply?.ok) return;

    /*
     * Joined. Read the booking once, because the stretch this socket was absent
     * for is exactly the stretch nobody heard about - and on a first connect
     * that stretch is "since the screen opened".
     */
    announce({ bookingId, kind: 'resync', at: new Date().toISOString() });
  });
}

function ensureSocket(): void {
  if (socket) return;

  socket = io(`${API_ORIGIN}/tracking`, {
    /*
     * WebSocket only, no long-polling upgrade.
     *
     * React Native has a real WebSocket; its XHR is a bridge away, and the
     * polling transport over it is a stream of requests through the same queue
     * the app's own calls use. There is also no fallback to protect here - if
     * the socket cannot be made at all, the polls carry on exactly as they do
     * today, which is the whole design above.
     */
    transports: ['websocket'],
    /*
     * A function, not an object, and that is load-bearing: it is called again
     * on every reconnection attempt, so a socket that drops after the access
     * token has rolled over comes back with the new one. An object literal
     * would carry whatever the app held at startup for the rest of the session.
     */
    auth: (send: (data: Record<string, unknown>) => void) =>
      send({ token: getAccessToken() ?? '' }),
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10_000,
  });

  /* Rooms do not survive a reconnection - the server has a new socket and has
     never heard of this one. Every watched booking is re-joined, and each join
     resyncs its screen. */
  socket.on('connect', () => {
    retryDelayMs = RETRY_BASE_MS;
    for (const bookingId of watchers.keys()) join(bookingId);
  });

  socket.on('booking', (message: BookingChanged) => {
    if (message?.bookingId) announce(message);
  });

  socket.on('connect_error', () => {
    /* `active` false means the server refused the handshake rather than the
       connection dropping - see the note on `retry`. */
    if (socket?.active || retry) return;
    retry = setTimeout(() => {
      retry = null;
      if (watchers.size > 0) socket?.connect();
    }, retryDelayMs);
    retryDelayMs = Math.min(RETRY_MAX_MS, retryDelayMs * 2);
  });
}

function teardown(): void {
  if (retry) {
    clearTimeout(retry);
    retry = null;
  }
  retryDelayMs = RETRY_BASE_MS;
  socket?.removeAllListeners();
  socket?.disconnect();
  socket = null;
}

/**
 * Hear about one booking until the returned function is called.
 *
 * Opens the socket on the first watcher and closes it after the last one, so an
 * app sitting on the catalogue with nothing live holds no connection.
 */
export function watchBooking(
  bookingId: string,
  listener: Listener,
): () => void {
  if (!bookingId) return () => {};

  const existing = watchers.get(bookingId);
  const listeners = existing ?? new Set<Listener>();
  listeners.add(listener);
  if (!existing) watchers.set(bookingId, listeners);

  ensureSocket();
  /* A socket that is already up has to be told about this booking now; one that
     is still connecting will pick it up in `connect` above. */
  if (socket?.connected && !existing) join(bookingId);

  return () => {
    const current = watchers.get(bookingId);
    if (!current) return;
    current.delete(listener);
    if (current.size > 0) return;

    watchers.delete(bookingId);
    if (socket?.connected) socket.emit('untrack', { bookingId });
    if (watchers.size === 0) teardown();
  };
}

/**
 * Drop the connection and forget everyone watching.
 *
 * For signing out. The token the socket authenticated with is no longer the
 * right one, and a socket left open on a previous session would keep pushing
 * that customer's bookings into an app that now belongs to somebody else.
 */
export function closeLiveBookings(): void {
  watchers.clear();
  teardown();
}
